/**
 * Finds the best insertion point for a blog diagram.
 *
 * Strategy: split the article into H2 sections, score each section by
 * plain-text length, penalize any section that already contains a visual
 * break (callout box, key takeaway, stat, etc.), then return the H2 id
 * of the section that immediately follows the densest plain-text run.
 *
 * Returns null if no good insertion point exists (e.g. short article,
 * every section already has a callout).
 */

const CALLOUT_CLASSES = [
  'key-takeaway',
  'auto-takeaway',
  'pro-tip',
  'warning',
  'insight',
  'stat-callout',
  'definition-box',
  'what-we-covered',
];

export function findDiagramInsertPoint(html: string): string | null {
  // Match every H2 with an injected id
  const h2Re = /<h2\s+id="([^"]+)"[^>]*>/gi;
  const matches = [...html.matchAll(h2Re)];

  // Need at least 2 H2s to have a "between" gap
  if (matches.length < 2) return null;

  // Build sections: content runs from this H2 to the next (or end)
  const sections = matches.map((m, i) => {
    const start = m.index!;
    const end = i + 1 < matches.length ? matches[i + 1].index! : html.length;
    return {
      id: m[1],
      nextId: i + 1 < matches.length ? matches[i + 1][1] : null,
      content: html.slice(start, end),
    };
  });

  // Score: plain-text character count, but 0 if section has any callout
  const scored = sections.map((s, i) => {
    // Can't insert after the last section
    if (i === sections.length - 1 || !s.nextId) return { s, score: -1 };

    const hasCallout = CALLOUT_CLASSES.some(cls =>
      s.content.includes(`class="${cls}"`) || s.content.includes(`class='${cls}'`)
    );
    if (hasCallout) return { s, score: 0 };

    // Strip tags and measure plain text length
    const text = s.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return { s, score: text.length };
  });

  const best = scored.reduce((a, b) => (b.score > a.score ? b : a));

  // If best score is 0 or negative, nothing suitable found
  if (best.score <= 0) return null;

  return best.s.nextId;
}
